import { useMemo } from 'react';
import type { TCSearcherTimelineItemDocument } from 'src/api/generated';
import {
  isMessageItem,
  isStatusHistoryItem,
} from 'src/modules/communications/utils/timeline/item-type-guards.util';
import { useMemoizedArrayData } from 'src/hooks/api/api';

interface UseFilteredTimelineItemsParams {
  items: TCSearcherTimelineItemDocument[];
  conversationId?: number;
}

/**
 * Filters timeline items based on conversation context.
 * Conversation-scoped items are filtered to only include those matching the current conversation ID.
 * Items without a conversation ID and non-conversation-scoped items are always included.
 *
 * @param items - Array of timeline item documents to filter
 * @param conversationId - Optional conversation ID for filtering conversation-scoped items
 * @returns Filtered array of timeline item documents
 */
export const useFilteredTimelineItems = ({
  items,
  conversationId,
}: UseFilteredTimelineItemsParams): TCSearcherTimelineItemDocument[] => {
  // Query data already shares unchanged objects. Revision metadata alone
  // cannot identify canonical corrections at the same timestamp.
  const stableItems = useMemoizedArrayData(items);

  return useMemo(() => {
    if (!conversationId) return stableItems;

    const conversationIdStr = conversationId.toString();

    return stableItems.filter((item) => {
      if (isStatusHistoryItem(item) || isMessageItem(item)) {
        if (!item.funnelConversationId) return true;

        return item.funnelConversationId === conversationIdStr;
      }

      return true;
    });
  }, [stableItems, conversationId]);
};
